import {MeterButton} from '@github-ui/copilot-chat/components/MeterButton'
import {ChatComposer, type ComposerAttachment} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {RepoPicker} from '@github-ui/agent-sessions-interface/components/RepoPicker'
import {useCallback, useImperativeHandle, useMemo, useRef} from 'react'
import {clsx} from 'clsx'
import type {ImageReference} from '@github-ui/copilot-chat-types'
import {FilePickerPanel} from '@github-ui/copilot-shared-ui/components/FilePickerPanel'
import {fetchRepos} from '@github-ui/copilot-shared-ui/repos/fetch-repos'

import {ModelPicker} from '../task-view/ModelPicker'
import {ReasoningEffortPicker} from '../task-view/ReasoningEffortPicker'
import {useMarkdownImageHandling} from '../../hooks/use-markdown-image-handling'
import {ImageReferenceToken} from '../ImageReferenceToken'
import styles from './TaskComposer.module.css'
import {EnvironmentStrip} from './environment-strip/EnvironmentStrip'
import {EnvironmentAttachmentControls, EnvironmentImageAttachments} from './EnvironmentAttachments'
import {MAX_AGENT_TASK_PROMPT_LENGTH} from '../../utils/constants'
import {QuotaBanner} from '../task-view/QuotaBanner'
import {ChronicleAutocomplete} from '../chronicle-autocomplete/ChronicleAutocomplete'
import {TaskComposerToolbar} from './TaskComposerToolbar'
import {useTaskComposer, useTaskComposerRepoState} from './use-task-composer'
import type {TaskComposerProps} from './shared'

const REFERENCE_ATTACHMENT_ID = 'reference'

function ImageAttachmentPreview({references}: {references: ImageReference[]}) {
  return (
    <ul className={styles.imageAttachments} aria-label="Attached images">
      {references.map(reference => (
        <li key={reference.id}>
          <ImageReferenceToken reference={reference} size="small" />
        </li>
      ))}
    </ul>
  )
}

export function TaskComposer(props: TaskComposerProps) {
  const {
    hideRepoSelector,
    chatInputRef,
    textAreaRef: textAreaRefFromProps,
    className,
    shouldClearTextOnSubmit = true,
    submitWithModifier = true,
    disableWhileSubmitting = false,
    isNewConversation,
    onEnvironmentSubmit,
    agentQuotaSnapshot,
    currentUserLogin,
    initialBranch,
    maxTextareaHeight,
  } = props

  const repoState = useTaskComposerRepoState()
  const images = useMarkdownImageHandling({
    textAreaRef: repoState.textAreaRef,
    repositoryId: repoState.selectedRepo?.id?.toString(),
  })

  const controller = useTaskComposer(props, {repoState, images})

  const {
    textAreaRef,
    setCurrentPrompt,
    currentPrompt,
    handleTextChange,
    handleChronicleSelect,
    setChronicleDismissed,
    chronicleSuggestions,
    onSubmit,
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isImageUploading,
    isBlockedByQuota,
    isAhpEnvironment,
    isSandboxEnvironment,
    isEnvironmentSubmitting,
    selectedRepo,
    selectedEnvironment,
    showCreateTaskStrip,
    showEnvironmentPicker,
    showSandboxOption,
    selectedBranch,
    setSelectedBranch,
    initialNameWithOwner,
    branchParam,
    handleRepoSelect,
    setSelectedEnvironment,
    reference,
    setReference,
    chatReference,
    imageReferences,
    shouldShowModelPicker,
    selectedModelId,
    selectModel,
    isModelSelectionReady,
    vendorFilters,
    isTokenBasedBillingEnabled,
    isReasoningEffortEnabled,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
    supportedReasoningEfforts,
    environmentAttachments,
  } = controller

  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
    textAreaRefFromProps ?? null,
    () => textAreaRef.current,
  )
  useImperativeHandle(chatInputRef, () => ({setText: setCurrentPrompt}), [setCurrentPrompt])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachButtonRef = useRef<HTMLButtonElement>(null)
  const openFilePicker = useCallback(() => fileInputRef.current?.click(), [])

  const attachments = useMemo<ComposerAttachment[] | undefined>(() => {
    const combined: ComposerAttachment[] = []
    if (chatReference && reference) {
      const label = reference.range
        ? `${reference.path}#L${reference.range.start}-L${reference.range.end}`
        : reference.path
      combined.push({
        id: REFERENCE_ATTACHMENT_ID,
        label,
        icon: reference.range ? 'snippet' : 'file',
        ...(chatReference.url ? {url: chatReference.url} : {}),
      })
    }
    if (isAhpEnvironment) combined.push(...environmentAttachments.attachments)
    return combined.length > 0 ? combined : undefined
  }, [chatReference, reference, isAhpEnvironment, environmentAttachments.attachments])

  const handleRemoveAttachment = useCallback(
    (id: string) => {
      if (id === REFERENCE_ATTACHMENT_ID) {
        setReference(null)
        return
      }
      environmentAttachments.handleRemoveAttachment(id)
    },
    [setReference, environmentAttachments],
  )

  const showTrailingModelControl = !showCreateTaskStrip && shouldShowModelPicker
  const showTrailingMeter = !isAhpEnvironment && !showCreateTaskStrip && isTokenBasedBillingEnabled

  return (
    <div className={styles.inputWrapper}>
      <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
      <div className={styles.autocompleteWrapper}>
        <ChronicleAutocomplete
          suggestions={chronicleSuggestions}
          onSelect={handleChronicleSelect}
          onDismiss={() => setChronicleDismissed(true)}
          textAreaRef={textAreaRef}
          currentText={currentPrompt}
        />
        <div className={clsx(className, isDraggedOver && styles.isDraggedOver)} {...dropTargetProps}>
          <ChatComposer
            value={currentPrompt}
            onValueChange={handleTextChange}
            textAreaRef={textAreaRef}
            onSubmit={onSubmit}
            placeholder="Give Copilot a background task to work on"
            ariaLabel="Coding task prompt"
            containerClassName={clsx(
              styles.composerMinHeight,
              isNewConversation && styles.composerNewConversationMinHeight,
            )}
            inputTextSize="responsive"
            submitWithModifier={submitWithModifier}
            clearOnSubmit={shouldClearTextOnSubmit}
            disableWhileSubmitting={disableWhileSubmitting || isAhpEnvironment}
            maxLength={MAX_AGENT_TASK_PROMPT_LENGTH}
            maxTextareaHeight={maxTextareaHeight}
            onPaste={onPaste}
            {...(attachments ? {attachments} : {})}
            hasAttachedContent={isAhpEnvironment && environmentAttachments.hasAttachments}
            {...(isAhpEnvironment
              ? {
                  onFilesAdded: environmentAttachments.handleFilesAdded,
                  onAttachFiles: environmentAttachments.openFilePicker,
                  onUploadFiles: openFilePicker,
                  onAttachRepo: environmentAttachments.openRepoPicker,
                  attachButtonRef,
                  ...(environmentAttachments.images.length > 0
                    ? {attachmentPreview: <EnvironmentImageAttachments controller={environmentAttachments} />}
                    : {}),
                }
              : imageReferences.length > 0
                ? {attachmentPreview: <ImageAttachmentPreview references={imageReferences} />}
                : {})}
            onRemoveAttachment={handleRemoveAttachment}
            disabled={
              isBlockedByQuota ||
              isEnvironmentSubmitting ||
              isImageUploading ||
              (isAhpEnvironment
                ? !onEnvironmentSubmit || !selectedEnvironment?.environmentId || (isSandboxEnvironment && !selectedRepo)
                : !isModelSelectionReady || !selectedRepo)
            }
            toolbarContent={<TaskComposerToolbar controller={controller} />}
            trailingToolbarContent={
              showTrailingModelControl || showTrailingMeter ? (
                <>
                  {showTrailingModelControl && (
                    <>
                      <div className={styles.pickerButtonWrapper}>
                        <ModelPicker
                          selectedModelId={selectedModelId}
                          onUpdateModel={selectModel}
                          vendorFilters={vendorFilters}
                          isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                        />
                      </div>
                      {isReasoningEffortEnabled && (
                        <ReasoningEffortPicker
                          selectedReasoningEffort={selectedReasoningEffort}
                          onUpdateReasoningEffort={setSelectedReasoningEffort}
                          supportedReasoningEfforts={supportedReasoningEfforts}
                          triggerClassName={styles.pickerButton}
                        />
                      )}
                    </>
                  )}
                  {showTrailingMeter && <MeterButton />}
                </>
              ) : undefined
            }
          />
        </div>
      </div>
      {isAhpEnvironment && (
        <>
          <EnvironmentAttachmentControls controller={environmentAttachments} fileInputRef={fileInputRef} />
          {environmentAttachments.filePickerOpen && (
            <FilePickerPanel
              isOpen
              onClose={environmentAttachments.closeFilePicker}
              onSelect={environmentAttachments.handleFilePickerSelect}
              repositoryLoader={fetchRepos}
            />
          )}
          <RepoPicker
            anchorRef={attachButtonRef}
            repositoryLoader={fetchRepos}
            selectedRepos={environmentAttachments.selectedRepos}
            onUpdateRepos={environmentAttachments.handleUpdateRepos}
            triggerOpen={environmentAttachments.repoPickerOpen}
            onClose={environmentAttachments.closeRepoPicker}
            anchorSide="outside-top"
          />
        </>
      )}
      {showCreateTaskStrip && (
        <EnvironmentStrip
          hideRepoSelector={hideRepoSelector}
          initialRepoNWO={initialNameWithOwner}
          initialBranch={initialBranch ?? branchParam ?? undefined}
          selectedRepo={selectedRepo || undefined}
          selectedBranch={selectedBranch}
          onRepoSelect={handleRepoSelect}
          onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
          currentPrompt={currentPrompt}
          currentUserLogin={currentUserLogin}
          selectedEnvironment={selectedEnvironment}
          onSelectEnvironment={setSelectedEnvironment}
          includeSandboxOption={showSandboxOption}
          showEnvironmentPicker={showEnvironmentPicker}
          branchSelectorDisabled={isSandboxEnvironment}
          disabled={isEnvironmentSubmitting}
        />
      )}
    </div>
  )
}
